import { useState, useCallback } from 'react';
import Cropper from 'react-easy-crop';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { getCroppedImg } from '@/lib/image';

interface Area {
  x: number;
  y: number;
  width: number;
  height: number;
}

interface ImageCropperBannerProps {
  image: string;
  onCrop: (croppedImage: Blob) => void;
  onCancel: () => void;
  aspectRatio: number;
}

export function ImageCropperBanner({ image, onCrop, onCancel, aspectRatio }: ImageCropperBannerProps) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [loading, setLoading] = useState(false);

  const onCropComplete = useCallback(
    (croppedArea: Area, croppedAreaPixels: Area) => {
      setCroppedAreaPixels(croppedAreaPixels);
    },
    []
  );

  const handleCrop = async () => {
    if (!croppedAreaPixels) return;

    try {
      setLoading(true);
      const croppedImage = await getCroppedImg(image, croppedAreaPixels);
      onCrop(croppedImage);
    } catch (error) {
      console.error('Error cropping image:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="relative h-64 w-full">
        <Cropper
          image={image}
          crop={crop}
          zoom={zoom}
          aspect={aspectRatio}
          onCropChange={setCrop}
          onCropComplete={onCropComplete}
          onZoomChange={setZoom}
          showGrid={false}
          style={{
            containerStyle: {
              borderRadius: '8px',
              overflow: 'hidden',
            },
          }}
        />
      </div>
      
      <div className="space-y-2">
        <label className="text-sm font-medium">Zoom</label>
        <Slider
          value={[zoom]}
          onValueChange={(value) => setZoom(value[0])}
          min={1}
          max={3}
          step={0.1}
          className="w-full"
        />
      </div>

      <div className="flex justify-end space-x-2">
        <Button variant="outline" onClick={onCancel} disabled={loading}>
          Cancelar
        </Button>
        <Button onClick={handleCrop} disabled={loading || !croppedAreaPixels}>
          {loading ? 'Processando...' : 'Aplicar'}
        </Button>
      </div>
    </div>
  );
}